import { Protocol } from '@uniswap/router-sdk'
import { Currency, CurrencyAmount, TradeType } from '@uniswap/sdk-core'
import { skipToken } from '@reduxjs/toolkit/query/react'

import {
    INTERNAL_ROUTER_PREFERENCE_PRICE,
    QuoteMethod,
    QuoteState,
    RouterPreference,
    SubmittableTrade,
    TradeState,
  } from 'state/routing/types'
import { useMemo } from 'react'
import useIsWindowVisible from 'hooks/useIsWindowVisible'
import { useRoutingAPIArguments } from 'lib/hooks/routing/useRoutingAPIArguments'
import ms from 'ms'
import { AVERAGE_L1_BLOCK_TIME } from 'constants/chains'
import { useGetKyberPriceQuoteQuery, useGetKyberPriceQueryState } from './sliceKyber'

const TRADE_NOT_FOUND = { state: TradeState.NO_ROUTE_FOUND, trade: undefined, currentData: undefined } as const
const TRADE_LOADING = { state: TradeState.LOADING, trade: undefined, currentData: undefined } as const


/**
 * Returns the best trade by invoking the kyber api on the client
 * @param tradeType whether the swap is an exact in/out
 * @param amountSpecified the exact amount to swap in/out
 * @param otherCurrency the desired output/payment currency
 */
export function usePriceKyberTrade<TTradeType extends TradeType>(
    skipFetch = false,
    tradeType: TTradeType,
    amountSpecified: CurrencyAmount<Currency> | undefined,
    otherCurrency: Currency | undefined,
    routerPreference: RouterPreference | typeof INTERNAL_ROUTER_PREFERENCE_PRICE,
    account?: string,
    protocolPreferences?: Protocol[],
): {
    state: TradeState
    trade?: SubmittableTrade
    currentTrade?: SubmittableTrade
    method?: QuoteMethod
    swapQuoteLatency?: number
} {
    const [currencyIn, currencyOut]: [Currency | undefined, Currency | undefined] = useMemo(
        () =>
          tradeType === TradeType.EXACT_INPUT
            ? [amountSpecified?.currency, otherCurrency]
            : [otherCurrency, amountSpecified?.currency],
        [amountSpecified, otherCurrency, tradeType],
      )
      
    const queryArgs = useRoutingAPIArguments({
        account,
        tokenIn: currencyIn,
        tokenOut: currencyOut,
        amount: amountSpecified,
        tradeType,
        routerPreference,
        protocolPreferences,
    })
    // skip all pricing and quote requests if the window is not focused
    const isWindowVisible = useIsWindowVisible()

    const { isError, data: tradeResult, error, currentData } = useGetKyberPriceQueryState(queryArgs)
    useGetKyberPriceQuoteQuery(skipFetch || !isWindowVisible ? skipToken : queryArgs, {
      // Price-fetching is informational and costly, so it's done less frequently.
      pollingInterval: routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE ? ms(`1m`) : AVERAGE_L1_BLOCK_TIME,
      // If latest quote from cache was fetched > 2m ago, instantly repoll for another instead of waiting for next poll period
      refetchOnMountOrArgChange: 2 * 60,
    })

    const isFetching = currentData !== tradeResult || !currentData

    return useMemo(() => {
        if (amountSpecified && otherCurrency && queryArgs === skipToken) {
        return {
            state: TradeState.STALE,
            trade: tradeResult?.trade,
            currentTrade: currentData?.trade,
            swapQuoteLatency: tradeResult?.latencyMs,
        }
        } else if (!amountSpecified || isError || queryArgs === skipToken) {
        return {
            state: TradeState.INVALID,
            trade: undefined,
            currentTrade: currentData?.trade,
            error: JSON.stringify(error),
        }
        } else if (tradeResult?.state === QuoteState.NOT_FOUND && !isFetching) {
        return TRADE_NOT_FOUND
        } else if (!tradeResult?.trade) {
        return TRADE_LOADING
        } else {
        return {
            state: isFetching ? TradeState.LOADING : TradeState.VALID,
            trade: tradeResult?.trade,
            currentTrade: currentData?.trade,
            swapQuoteLatency: tradeResult?.latencyMs,
        }
        }
        }, [
        amountSpecified,
        error,
        isError,
        isFetching,
        queryArgs,
        tradeResult?.latencyMs,
        tradeResult?.state,
        tradeResult?.trade,
        currentData?.trade,
        otherCurrency,
    ])

}